import { HttpContext, HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, EMPTY, mergeMap, Observable, throwError } from 'rxjs';
import { catchError, concatMap, filter, finalize, first, map, take, tap } from 'rxjs/operators';

import { AUTH_PROVIDER_REQUEST } from './providers/auth-provider';
import { AuthService } from './services/auth.service';
import { AuthApiActions, AuthPageActions } from './state/actions';
import { AuthFacade } from './state/facades/auth.facade';

export const BYPASS_HTTP_CONTEXT_BUSINESS_PROFILE_ID_HEADER = new HttpContextToken<boolean>(() => false);

export const bypassHttpContextBusinessProfileIdHeader = () => new HttpContext().set(BYPASS_HTTP_CONTEXT_BUSINESS_PROFILE_ID_HEADER, true);

/** Local static files (SVG icon sets, i18n JSON…) never need auth headers nor a token refresh. */
const STATIC_ASSET_URL = /^\/?assets\//;

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  /** Single refresh queue (Cognito and SSO): emits the refreshed idToken, `null` while a refresh is in flight. */
  tokenRefreshed$ = new BehaviorSubject<string | null>(null);
  private isRefreshingToken = false;
  private readonly authService: AuthService = inject(AuthService);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  private readonly store: Store = inject(Store);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.context.get(AUTH_PROVIDER_REQUEST) || STATIC_ASSET_URL.test(request.url)) {
      return next.handle(request);
    }
    return combineLatest([
      this.authFacade.loggedIn$,
      this.authFacade.loggedInWithSSO$,
      this.authFacade.idToken$,
      this.authFacade.selectedBusinessProfileId$,
      this.authFacade.refreshToken$,
      this.authFacade.ssoTokenExpiresAt$
    ]).pipe(
      first(),
      mergeMap(([loggedIn, loggedInWithSSO, idToken, businessProfileId, refreshToken, ssoTokenExpiresAt]) => {
        if (!loggedIn) {
          return next.handle(request);
        }
        // Refresh token before calling request
        const isTokenExpired = loggedInWithSSO ? this.authService.ssoTokenExpired(ssoTokenExpiresAt) : !!idToken && this.authService.tokenExpired(idToken);
        if (isTokenExpired) {
          return loggedInWithSSO
            ? this.handleRefreshSsoToken(refreshToken, businessProfileId, request, next)
            : this.handleRefreshToken(businessProfileId, request, next);
        }
        return next.handle(this.setRequestHeaders(idToken, businessProfileId, request)).pipe(
          catchError((err: unknown) => {
            if (err instanceof HttpErrorResponse && err.error?.type === 'BusinessProfileNotCoveredByUserEntity') {
              this.store.dispatch(AuthPageActions.signOut());
            }
            return throwError(() => err);
          })
        );
      })
    );
  }

  private setRequestHeaders(idToken: string, businessProfileId: string, req: HttpRequest<unknown>): HttpRequest<unknown> {
    let request = req;
    if (!businessProfileId || req.context.get(BYPASS_HTTP_CONTEXT_BUSINESS_PROFILE_ID_HEADER)) {
      request = req.clone({
        setHeaders: {
          Authorization: `Bearer ${idToken}`
        }
      });
    } else {
      request = req.clone({
        setHeaders: {
          Authorization: `Bearer ${idToken}`,
          'business-profile-id': `${businessProfileId}`
        }
      });
    }
    return request;
  }

  private handleRefreshToken(businessProfileId: string, req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const refreshedIdToken$ = (): Observable<string> =>
      this.authService.refreshToken().pipe(
        tap((session) => this.store.dispatch(AuthApiActions.refreshTokenSuccess({ session }))),
        map((session) => session.tokens.idToken)
      );
    return this.refreshThenSend(refreshedIdToken$, (error) => AuthApiActions.refreshTokenFailure({ error }), businessProfileId, req, next);
  }

  private handleRefreshSsoToken(refreshToken: string, businessProfileId: string, req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const refreshedIdToken$ = (): Observable<string> =>
      this.authService.refreshSsoTokens(refreshToken).pipe(
        tap(({ idToken, accessToken, tokenType, expiresIn }) =>
          this.store.dispatch(AuthApiActions.refreshSsoTokensSuccess({ idToken, accessToken, tokenType, expiresIn }))
        ),
        map(({ idToken }) => idToken)
      );
    return this.refreshThenSend(refreshedIdToken$, (error) => AuthApiActions.refreshSsoTokensFailure({ error }), businessProfileId, req, next);
  }

  /**
   * Runs a single refresh at a time: concurrent requests wait on `tokenRefreshed$` and are replayed with the new idToken.
   * A refresh failure signs the user out.
   */
  private refreshThenSend(
    refreshedIdToken$: () => Observable<string>,
    failureAction: (error: HttpErrorResponse | Error) => Action,
    businessProfileId: string,
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshingToken) {
      return this.tokenRefreshed$.pipe(
        filter((idToken): idToken is string => !!idToken),
        take(1),
        concatMap((idToken) => next.handle(this.setRequestHeaders(idToken, businessProfileId, req)))
      );
    }

    this.isRefreshingToken = true;

    // Reset here so that the following requests wait until the token
    // comes back from the refresh call.
    this.tokenRefreshed$.next(null);

    return refreshedIdToken$().pipe(
      concatMap((idToken) => {
        this.tokenRefreshed$.next(idToken);
        return next.handle(this.setRequestHeaders(idToken, businessProfileId, req));
      }),
      catchError((error: HttpErrorResponse | Error) => {
        // Disconnect the user and redirect to login page
        // in case we got an error while refreshing the token
        this.store.dispatch(failureAction(error));
        this.store.dispatch(AuthPageActions.signOut());
        return EMPTY;
      }),
      finalize(() => {
        this.isRefreshingToken = false;
      })
    );
  }
}
